import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import { COLOR_SYSTEM } from "components/design/design-system";
import { AIReview, getPromptTypeKoreanName } from "common/models/types";
import { eventLog } from "utils/AmplitudeUtil";
import SectionHeader from "./SectionHeader";

const ReportCard = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  padding: 24px;
`;

const ReportList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReportRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ReportNumber = styled.div`
  flex-shrink: 0;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-variant-numeric: tabular-nums;
`;

const ReportItem = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  padding: 16px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${COLOR_SYSTEM.get("Skyblue")?.[200]};
    background: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  }
`;

const ReportItemContent = styled.div`
  display: flex;
  align-items: center;
  padding-right: 110px;
`;

const DateBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  white-space: nowrap;
`;

const ReportIconWrapper = styled.div`
  background: ${COLOR_SYSTEM.get("Yellow")?.[100]};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
`;

const ReportDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReportDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ReportDetailRow = styled.div`
  display: flex;
  align-items: center;
`;

const ViewAllLink = styled.button`
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 2px;
  border-radius: 4px;
  transition: background 0.2s ease;

  &:hover {
    background: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  }
`;

const formatDate = (date?: Date | string): string => {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const getPatientName = (report: AIReview): string => {
  return report.patientId ? `환자${report.patientId}` : "환자 정보 없음";
};

interface DashboardPinnedReportsProps {
  reports: AIReview[];
  totalCount: number;
  onSelectReport: (patientId: number, reportId: number) => void;
}

/**
 * 고정한 보고서 전용 섹션.
 * 상위에서 이미 Redux의 pinnedReviews id 배열 + reviews 해시를 조합해 AIReview[]로 전달.
 * 0건이면 이 컴포넌트를 렌더하지 않음 (4.2 규칙).
 * 대시보드는 상위 10건만 표시하며 전체 목록은 /mypage/ai-reviews 페이지로 위임.
 */
const DashboardPinnedReports: React.FC<DashboardPinnedReportsProps> = ({
  reports,
  totalCount,
  onSelectReport,
}) => {
  const navigate = useNavigate();

  if (!reports || reports.length === 0) return null;

  const handleItemClick = (report: AIReview) => {
    eventLog("대시보드핀보고서선택", {
      reportId: report.id,
      patientId: report.patientId,
    });
    if (report.patientId && report.id) {
      onSelectReport(report.patientId, report.id);
    }
  };

  const handleViewAll = () => {
    eventLog("대시보드_목록_보기_이동", { tab: "pinned" });
    navigate("/mypage/ai-reviews?tab=pinned");
  };

  const isAllShown = reports.length >= totalCount;
  const descriptionText = isAllShown
    ? `고정한 보고서 총 ${totalCount}건입니다`
    : `고정한 보고서 총 ${totalCount}건 중 최근 핀한 ${reports.length}건을 보여드리고 있어요`;

  return (
    <ReportCard>
      <SectionHeader
        title="고정한 보고서"
        description={descriptionText}
        rightSlot={
          <ViewAllLink onClick={handleViewAll}>
            전체 보기
            <Icon name="Chevron Right" size={14} color={COLOR_SYSTEM.get("Skyblue")?.[500]} />
          </ViewAllLink>
        }
      />
      <ReportList>
        {reports.map((report, index) => (
          <ReportRow key={report.id}>
            <ReportNumber>{index + 1}</ReportNumber>
            <ReportItem onClick={() => handleItemClick(report)}>
            <DateBadge>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
                핀 {formatDate(report.pinnedAt)}
              </Text>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
                생성 {formatDate(report.reviewDate || report.createdAt)}
              </Text>
            </DateBadge>
            <ReportItemContent>
              <ReportIconWrapper>
                <Icon
                  name="Pin 02 Filled"
                  size={20}
                  color={COLOR_SYSTEM.get("Yellow")?.[500]}
                />
              </ReportIconWrapper>
              <ReportDetails>
                <ReportDetailHeader>
                  <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[800]}>
                    보고서 #{report.id} - {getPatientName(report)}
                  </Text>
                </ReportDetailHeader>
                <ReportDetailRow>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                    보고서 종류: {getPromptTypeKoreanName(report.promptType)}
                  </Text>
                </ReportDetailRow>
                {report.memo && (
                  <ReportDetailRow>
                    <Text textType="Body2" color={COLOR_SYSTEM.get("Skyblue")?.[500]}>
                      메모: {report.memo}
                    </Text>
                  </ReportDetailRow>
                )}
              </ReportDetails>
            </ReportItemContent>
            </ReportItem>
          </ReportRow>
        ))}
      </ReportList>
    </ReportCard>
  );
};

export default DashboardPinnedReports;
